/**
 * Vista Consolidada Multi-Empresa
 * Muestra KPIs, tendencia 6 meses y detalle por empresa en una moneda unificada.
 */
import { useState, useRef } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { useNavigate } from 'react-router-dom'
import { Building2, DollarSign, TrendingUp, CreditCard, AlertTriangle, Users, ArrowLeft, LogOut, User, KeyRound } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { useAuthStore } from '@/store/authStore'
import { useLogout } from '@/modules/auth/hooks/useAuth'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useConsolidatedMetrics } from '../hooks/useConsolidation'
import DatePicker from '@/components/DatePicker'
import { currentMonthRange } from '@/lib/utils'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

const formatMonth = (ym: string) => {
  const [y, m] = ym.split('-')
  return `${MONTH_NAMES[parseInt(m) - 1]} ${y.slice(2)}`
}

const fmtMoney = (v: string | number, currency = 'USD') =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(typeof v === 'string' ? parseFloat(v) : v)

const fmtAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`
  return v.toString()
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4']

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'CLP', label: 'CLP' },
  { value: 'PEN', label: 'PEN' },
  { value: 'EUR', label: 'EUR' },
]

// ─── KPI Card ────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon: Icon,
  bgCls,
  iconCls,
}: {
  label: string
  value: string
  icon: React.ElementType
  bgCls: string
  iconCls: string
}) {
  return (
    <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${bgCls} flex items-center justify-center`}>
          <Icon size={20} className={iconCls} />
        </div>
        <div>
          <p className="text-xs text-content-muted">{label}</p>
          <p className="text-lg font-bold text-content">{value}</p>
        </div>
      </div>
    </div>
  )
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-surface-muted" />
              <div className="space-y-2 flex-1">
                <div className="h-3 bg-surface-muted rounded w-20" />
                <div className="h-5 bg-surface-muted rounded w-28" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-surface rounded-xl border border-divider p-5 h-72">
        <div className="h-4 bg-surface-muted rounded w-48 mb-4" />
        <div className="h-full bg-surface-muted rounded" />
      </div>
      <div className="bg-surface rounded-xl border border-divider p-5 h-48" />
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function ConsolidatedDashboardPage() {
  // El rango del mes en curso sale del helper compartido y no de un
  // `toISOString()` propio: eso devuelve UTC, y en Chile desde las 20:00 el día
  // UTC ya es el siguiente, así que la noche del último día del mes el rango
  // saltaba entero al mes entrante y la vista salía vacía.
  const { from: firstOfMonth, to: today } = currentMonthRange()
  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)
  const [currency, setCurrency] = useState('USD')

  const { data, isLoading, isError } = useConsolidatedMetrics({
    date_from: dateFrom,
    date_to: dateTo,
    currency,
  })

  // Build chart data from tenants' trend_6m
  const chartData =
    data?.by_tenant?.[0]?.metrics_converted.trend_6m.map((t, i) => {
      const point: Record<string, unknown> = { month: formatMonth(t.month) }
      data.by_tenant.forEach((tenant, j) => {
        point[tenant.name] = parseFloat(
          tenant.metrics_converted.trend_6m[i]?.revenue || '0',
        )
      })
      return point
    }) || []

  const tenantNames = data?.by_tenant.map((t) => t.name) || []

  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { mutate: logout } = useLogout()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(userMenuOpen, [disparadorRef, panelRef], () => setUserMenuOpen(false))

  return (
    <div className="min-h-screen bg-surface-muted">
      {/* Topbar */}
      <header className="flex items-center justify-between h-14 sm:h-16 px-4 sm:px-6 bg-surface border-b border-divider sticky top-0 z-30">
        <button
          onClick={() => navigate('/auth/select-tenant')}
          className="flex items-center gap-2 text-sm text-content-muted hover:text-primary transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="hidden sm:inline">Volver a mis empresas</span>
        </button>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button ref={disparadorRef}
              onClick={() => setUserMenuOpen(p => !p)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-soft transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
                <span className="text-white text-xs font-semibold">
                  {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <span className="text-sm font-medium text-content hidden sm:inline">
                {user?.full_name || user?.email}
              </span>
            </button>

            {userMenuOpen && (
              <>
                <div ref={panelRef}
                  className="absolute right-0 top-12 z-50 bg-popover border border-divider rounded-xl shadow-lg py-1 min-w-[200px] whitespace-nowrap"
                  style={{ backgroundColor: 'rgb(var(--color-popover))' }}
                >
                  <button
                    onClick={() => { navigate('/auth/select-tenant'); setUserMenuOpen(false) }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-content hover:bg-soft"
                  >
                    <Building2 size={15} />Mis empresas
                  </button>
                  <div className="border-t border-divider my-1" />
                  <button
                    onClick={() => logout()}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-danger-content-soft hover:bg-danger-surface"
                  >
                    <LogOut size={15} />Cerrar sesión
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content">Vista Consolidada</h1>
          <p className="text-sm text-content-muted mt-0.5">
            {data
              ? `Resumen financiero de ${data.tenants_count} empresa${data.tenants_count !== 1 ? 's' : ''}`
              : 'Cargando datos consolidados...'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="consolidateddashboardpage-desde" className="text-xs text-content-muted whitespace-nowrap">Desde</label>
            <DatePicker id="consolidateddashboardpage-desde" value={dateFrom} onChange={(v) => setDateFrom(v)} className="w-36" />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="consolidateddashboardpage-hasta" className="text-xs text-content-muted whitespace-nowrap">Hasta</label>
            <DatePicker id="consolidateddashboardpage-hasta" value={dateTo} onChange={(v) => setDateTo(v)} className="w-36" />
          </div>
          <select aria-label="Moneda"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="border border-divider rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && <Skeleton />}

      {/* Error */}
      {isError && (
        <div className="text-center py-16">
          <AlertTriangle size={40} className="mx-auto text-danger-solid mb-3" />
          <p className="text-content-muted font-medium">Error al cargar datos consolidados</p>
          <p className="text-sm text-content-subtle mt-1">Verifica tu conexion e intenta nuevamente.</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && data && data.tenants_count === 0 && (
        <div className="text-center py-16">
          <Building2 size={40} className="mx-auto text-content-subtle mb-3" />
          <p className="text-content-muted font-medium">Sin acceso consolidado</p>
          <p className="text-sm text-content-subtle mt-1 max-w-md mx-auto">
            No tienes acceso consolidado a ninguna empresa. Solicita el permiso
            &lsquo;Consolidacion&rsquo; al administrador de cada empresa.
          </p>
        </div>
      )}

      {/* Data */}
      {!isLoading && !isError && data && data.tenants_count > 0 && (
        <>
          {/* KPI Cards */}
          <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-4 mb-6">
            <KpiCard
              label="Revenue Total"
              value={fmtMoney(data.summary.revenue, currency)}
              icon={DollarSign}
              bgCls="bg-primary-100"
              iconCls="text-primary"
            />
            <KpiCard
              label="Compras Total"
              value={fmtMoney(data.summary.purchases, currency)}
              icon={TrendingUp}
              bgCls="bg-orange-100 dark:bg-orange-900/40"
              iconCls="text-orange-600 dark:text-orange-300"
            />
            <KpiCard
              label="Cobrado"
              value={fmtMoney(data.summary.collected, currency)}
              icon={CreditCard}
              bgCls="bg-success-surface-strong"
              iconCls="text-success-content-soft"
            />
            <KpiCard
              label="Pendiente"
              value={fmtMoney(data.summary.outstanding, currency)}
              icon={AlertTriangle}
              bgCls="bg-warning-surface-strong"
              iconCls="text-warning-content-soft"
            />
            <KpiCard
              label="Vencido"
              value={fmtMoney(data.summary.overdue, currency)}
              icon={AlertTriangle}
              bgCls="bg-danger-surface-strong"
              iconCls="text-danger-content-soft"
            />
            <KpiCard
              label={`N. Empresas`}
              value={String(data.tenants_count)}
              icon={Building2}
              bgCls="bg-info-surface-strong"
              iconCls="text-info-content-soft"
            />
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 mb-6 shadow-card">
              <h2 className="text-sm font-semibold text-content mb-4">
                Tendencia 6 Meses por Empresa
              </h2>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--color-divider))" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: 'rgb(var(--color-content-muted))' }}
                    axisLine={{ stroke: 'rgb(var(--color-divider))' }}
                  />
                  <YAxis
                    tickFormatter={fmtAxis}
                    tick={{ fontSize: 12, fill: 'rgb(var(--color-content-muted))' }}
                    axisLine={{ stroke: 'rgb(var(--color-divider))' }}
                  />
                  <Tooltip
                    formatter={(value: number) => fmtMoney(value, currency)}
                    contentStyle={{
                      borderRadius: '0.75rem',
                      border: '1px solid rgb(var(--color-divider))',
                      backgroundColor: 'rgb(var(--color-popover))',
                      color: 'rgb(var(--color-content))',
                      fontSize: '0.75rem',
                    }}
                    labelStyle={{ color: 'rgb(var(--color-content))' }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '0.75rem', paddingTop: '0.5rem' }}
                  />
                  {tenantNames.map((name, idx) => (
                    <Bar
                      key={name}
                      dataKey={name}
                      stackId="revenue"
                      fill={COLORS[idx % COLORS.length]}
                      radius={idx === tenantNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Table */}
          <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
            <div className="px-5 py-4 border-b border-divider">
              <h2 className="text-sm font-semibold text-content">Detalle por Empresa</h2>
            </div>
            <DataTable
              columns={[
                { key: 'name', header: 'Empresa', render: t => <span className="font-medium text-content">{t.name}</span> },
                { key: 'currency', header: 'Moneda' },
                ...(['revenue', 'purchases', 'collected', 'outstanding', 'overdue'] as const).map(k => ({
                  key: k,
                  header: { revenue: 'Revenue', purchases: 'Compras', collected: 'Cobrado', outstanding: 'Pendiente', overdue: 'Vencido' }[k],
                  numeric: true,
                  sortValue: (t: typeof data.by_tenant[number]) => Number(t.metrics_converted[k]),
                  // El monto va convertido a la moneda del consolidado, con el
                  // original debajo cuando el tenant factura en otra: sin ese
                  // segundo renglón no se puede contrastar contra su propio ERP.
                  render: (t: typeof data.by_tenant[number]) => (
                    <>
                      <div className="text-content">{fmtMoney(t.metrics_converted[k], currency)}</div>
                      {t.currency !== currency && (
                        <div className="text-xs text-content-subtle">{fmtMoney(t.metrics[k], t.currency)}</div>
                      )}
                    </>
                  ),
                })),
              ]}
              rows={data.by_tenant}
              rowKey={t => t.schema}
              footerLabel="Total"
              // 🔴 El total estaba como un <tr> más DENTRO del tbody: con el
              // orden por clic se habría mezclado entre las empresas. En el
              // <tfoot> queda fuera del orden por construcción.
              footer={{
                currency,
                revenue: fmtMoney(data.summary.revenue, currency),
                purchases: fmtMoney(data.summary.purchases, currency),
                collected: fmtMoney(data.summary.collected, currency),
                outstanding: fmtMoney(data.summary.outstanding, currency),
                overdue: fmtMoney(data.summary.overdue, currency),
              }}
              // El panel y su encabezado «Detalle por Empresa» ya los pone el
              // contenedor.
              surface={false}
              minWidth={900}
              caption="Detalle consolidado por empresa"
            />
          </div>
        </>
      )}
    </div>
    </div>
  )
}
